<template>
  <TwoFactorAuthForm v-if="showTwoFactorAuth" @close="showTwoFactorAuth = false" @success="onSuccess" />
  <template v-else>
    <p v-if="isModal" :class="$style.backLink"><button @click.prevent="close">&lt; 戻る</button></p>
    <div :class="$style.content">
      <p :class="$style.signup"><a :href="`/signup?from=${from}`" @click.prevent="toggle">会員登録はこちら</a></p>
      <h1 :class="$style.pageTitle">ログイン</h1>
      <div v-if="oauthProvider && socialProvider(oauthProvider)" :class="$style.socialConnected">
        <!-- eslint-disable vue/no-v-html -->
        <div :class="$style.socialConnectedSvg" v-html="socialProvider(oauthProvider)!.svg"></div>
        <!-- eslint-enable vue/no-v-html -->
        <div>{{ socialProvider(oauthProvider)!.label }}連携しています</div>
      </div>
      <Form :validation-schema="validationSchema" @invalid-submit="scrollToError" @submit="onSubmit">
        <ul :class="$style.formInner">
          <li>
            <ITextField v-model.trim="formData.email" name="email" title="メールアドレス" type="email" autocomplete="email" />
          </li>
          <li>
            <ITextField v-model.trim="formData.password" name="password" title="パスワード" type="password" autocomplete="current-password" />
          </li>
        </ul>
        <div :class="$style.remember">
          <label><input v-model="formData.remember" type="checkbox" /> ログイン情報を保持する</label>
        </div>
        <IButton type="primary" native-type="submit" :class="$style.button">ログインする</IButton>
        <p :class="$style.forget"><a href="/signin/forget/password" :class="$style.forgetLink">パスワードをお忘れの方</a></p>
        <template v-if="!isWebView && oauthProvider === undefined">
          <p :class="$style.or"><span :class="$style.orInner">または</span></p>
          <ul :class="$style.socialBtns">
            <li v-for="item in connectArr" :key="item.id" :class="$style.socialBtn">
              <!-- eslint-disable vue/no-v-html -->
              <a :href="item.url" :class="$style.socialBtnLink" rel="nofollow"
                ><span :class="$style.socialBtnSvg"><span v-html="item.svg"></span></span>{{ item.label }}でログイン</a
              >
              <!-- eslint-enable vue/no-v-html -->
            </li>
          </ul>
        </template>
        <p>
          <a href="https://help.iichi.com/hc/ja/articles/360000013721" target="_blank">ログインでお困りの方</a>
        </p>
      </Form>
    </div>
  </template>
</template>

<script setup lang="ts">
import { passwordSchema } from '@iichi/schema-api-v2/entities/password';
import { memberSchema } from '@iichi/schema-models';
import { toTypedSchema } from '@vee-validate/zod';
import { Form } from 'vee-validate';
import { ref, reactive, computed } from 'vue';
import * as z from 'zod';
import { scrollToError } from '@/js/lib/helper/validate';
import { signin } from '@/js/lib/api/v1/user';
import { useCommonStore } from '@/js/lib/store/common';
import { useUserStore } from '@/js/lib/store/user';
import { info, loading } from '@/js/lib/ui';
import { socialProviders } from '@/js/lib/ui/signin-signup';
import IButton from '@/js/components/molecules/IButton.vue';
import ITextField from '@/js/components/molecules/ITextField.vue';
import TwoFactorAuthForm from './TwoFactorAuthForm.vue';

interface FormData {
  email: string;
  password: string;
  remember: boolean;
}

const props = withDefaults(
  defineProps<{
    email?: string;
    oauthProvider?: string;
    isModal?: boolean;
    from?: string;
  }>(),
  {
    email: '',
    oauthProvider: undefined,
    from: '',
  },
);
const emit = defineEmits<{
  (e: 'toggle'): void;
  (e: 'close'): void;
  (e: 'submit'): void;
}>();
const { isWebView } = useCommonStore();
const userStore = useUserStore();
const formData = reactive<FormData>({
  email: props.email,
  password: '',
  remember: true,
});
const showTwoFactorAuth = ref(false);
const validationSchema = computed(() =>
  toTypedSchema(
    z.object({
      email: memberSchema.shape.email,
      password: passwordSchema,
    }),
  ),
);
const connectArr = computed(() =>
  socialProviders.map((item) => {
    const from = props.from ? `?from=${props.from}` : '';
    const url = `/signin/with/${item.id}${from}`;
    return {
      id: item.id,
      label: item.label,
      svg: item.svg,
      url,
    };
  }),
);
const socialProvider = computed(() => (provider: string) => socialProviders.find((p) => p.id === provider));
const onSubmit = async () => {
  loading({
    asyncFunc: async () => {
      const trustedDeviceId = (() => {
        try {
          return localStorage.getItem('trusted-device-id') || undefined;
        } catch {
          return;
        }
      })();
      try {
        const res = await signin({
          email: formData.email,
          password: formData.password,
          remember: formData.remember,
          trustedDeviceId,
        });
        if (res.requires2Fa) {
          showTwoFactorAuth.value = true;
          return;
        }
        onSuccess();
      } catch {
        info('メールアドレス、もしくはパスワードが違います。', { fadeOut: true });
      }
    },
  });
};
const toggle = (event: Event) => {
  if (props.isModal) {
    emit('toggle');
  } else {
    window.location.href = (event.target as HTMLAnchorElement).href;
  }
};
const onSuccess = () => {
  userStore.setSignined();
  if (props.isModal) {
    emit('submit');
  } else {
    window.location.href = props.from || '/';
  }
};
const close = () => {
  emit('close');
};
</script>

<style module lang="scss">
.backLink {
  left: 20px;
  position: absolute;
  top: 25px;

  > button {
    font-size: 16px;

    @media (--sp) {
      font-size: 14px;
    }
  }
}

.content {
  margin: 0 auto;
  max-width: 480px;
  text-align: center;
  width: 100%;

  @media (--pc) {
    min-height: 840px;
  }
}

.signup {
  font-weight: bold;
  margin-bottom: 16px;
  text-align: right;
}

.pageTitle {
  font-size: 24px;
  margin-bottom: 30px;
}

.socialConnected {
  align-items: center;
  display: flex;
  margin-bottom: 25px;

  .socialConnectedSvg {
    margin-right: 8px;

    > svg {
      vertical-align: bottom;
    }
  }
}

.formInner > li {
  margin-bottom: 20px;
}

.remember {
  margin-bottom: 30px;
  text-align: left;

  label {
    cursor: pointer;
  }
}

.button {
  margin-bottom: 20px;
}

.forget {
  margin-bottom: 20px;
  text-align: center;
}

.forgetLink {
  text-decoration: underline;
}

.or {
  display: flex;
  justify-content: center;
  margin-bottom: 14px;
  padding: 10px 0;
  position: relative;

  &::before {
    border-top: 1px solid var(--color-black);
    content: '';
    position: absolute;
    top: 50%;
    width: 100%;
  }

  .orInner {
    background-color: var(--color-white);
    display: inline-block;
    padding: 0 10px;
    position: relative;
  }
}

.socialBtns {
  padding-bottom: 10px;
}

.socialBtn {
  margin-bottom: 14px;
  position: relative;

  .socialBtnLink {
    border: 1px solid var(--color-black);
    border-radius: 4px;
    display: block;
    padding: 16px;
    text-decoration: none;

    &:hover {
      opacity: 0.8;
    }

    .socialBtnSvg {
      left: 18px;
      position: absolute;
      width: 24px;
    }
  }
}
</style>
